import React, { useEffect, useMemo } from 'react';
import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import { useField } from 'react-final-form';
import { GET_MANY_REFERENCE, useQueryWithStore } from 'react-admin';

import { Input, Select } from 'components/form';
import { StateOrProvinceDTO } from '../../types';
import { DictionaryStatus } from './types';

const useStyles = makeStyles((theme) => ({
  dictionaryStatus: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: theme.spacing(8),
  },
  dictionaryError: {
    color: theme.palette.error.main,
  },
}));

const StateOptionsField = ({
  countryId,
  name,
  validate,
  onStatusChange,
}: {
  countryId: string;
  name: string;
  validate: (value: any) => string | undefined;
  onStatusChange: (status: DictionaryStatus) => void;
}) => {
  const classes = useStyles();
  const {
    data: statesData,
    loading,
    error,
  } = useQueryWithStore({
    type: GET_MANY_REFERENCE,
    resource: 'states',
    payload: { countryId },
  });
  const states = useMemo<StateOrProvinceDTO[]>(
    () => (Array.isArray(statesData) ? statesData : []),
    [statesData]
  );
  const {
    input: { value, onChange },
  } = useField(name);

  useEffect(() => {
    onStatusChange({ countryId, loading, error, optionsCount: states.length });
  }, [countryId, error, loading, onStatusChange, states.length]);

  useEffect(() => {
    if (
      !loading &&
      !error &&
      states.length &&
      value &&
      !states.some((state) => state.code === value)
    ) {
      onChange(null);
    }
  }, [error, loading, onChange, states, value]);

  if (loading) {
    return (
      <div className={classes.dictionaryStatus}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <Typography className={classes.dictionaryError} variant="body2">
        Unable to load states
      </Typography>
    );
  }

  if (!states.length) {
    return <Input name={name} label="State" validate={validate} />;
  }

  return (
    <Select
      name={name}
      label="State"
      options={states.map((state) => ({ value: state.code, label: state.name }))}
      validate={validate}
    />
  );
};

export default StateOptionsField;
